import { useMemo } from "react"
import type { BinaryOperator, Filter, Query } from "@cubejs-client/core"
import dayjs from "dayjs"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"

const PREFIX = "company_media_metrics_ads_by_product"

/**
 * Product-grain paid-media metrics, fetched SEPARATELY from the mega_feed view
 * and merged client-side by Shopify handle.
 *
 * Why not join it into the view: `media_metrics_paid_media_by_ad_product` is
 * provisioned per tenant and is absent for several live companies. A Cube view
 * joins unconditionally, so including it there made every mega_feed query fail
 * with "Unknown table expression identifier" rather than returning null metrics.
 * Queried on its own, the capabilities gate inside useCubeQuery skips it when
 * the table is missing, and the spend columns simply come back empty.
 *
 * Two correctness rules come with this cube:
 *  - match_type MUST be pinned to exactly one value. 'all' is a superset of
 *    'main', so querying both double-counts every primary match.
 *  - Metrics are EXPOSURE-attributed: an ad matching five products contributes
 *    its full spend to all five. Per-product values are right; a grand total
 *    is not, which is why nothing here is summed into a page-level figure.
 */

export const AD_METRICS_MAP = {
  spend: "Ad Spend",
  brand_value_agg: "Brand Value",
  estimated_d180_brand_value: "Modeled Brand Value",
  modeled_revenue: "Modeled Revenue",
  purchases: "Platform Revenue",
  ad_count_with_spend: "Active Ads",
}

export type TAdMetricKey = keyof typeof AD_METRICS_MAP

export const AD_METRIC_KEYS = Object.keys(AD_METRICS_MAP) as TAdMetricKey[]

export const MONETARY_AD_METRICS: TAdMetricKey[] = [
  "spend",
  "brand_value_agg",
  "estimated_d180_brand_value",
  "modeled_revenue",
  "purchases",
]

export type TAdMetricsByHandle = Record<string, Record<string, unknown>>

export default function useAdMetrics({
  handles,
  dateRange,
  matchType = "main",
  enabled = true,
}: {
  handles: string[]
  dateRange: [Date, Date]
  matchType?: "main" | "all"
  enabled?: boolean
}) {
  // Only the handles on the current page, so this stays a small lookup rather
  // than pulling the whole account's ad history.
  const handleKey = handles.join("|")

  const query = useMemo<Query>(() => {
    const filters: Filter[] = [
      {
        member: `${PREFIX}.match_type`,
        operator: "equals" as BinaryOperator,
        values: [matchType],
      },
    ]

    if (handles.length > 0) {
      filters.push({
        member: `${PREFIX}.product_handle`,
        operator: "equals" as BinaryOperator,
        values: handles,
      })
    }

    return {
      dimensions: [`${PREFIX}.product_handle`],
      measures: AD_METRIC_KEYS.map(metric => `${PREFIX}.${metric}`),
      timeDimensions: [
        {
          dimension: `${PREFIX}.md_date`,
          dateRange: [
            dayjs(dateRange[0]).startOf("day").format("YYYY-MM-DD"),
            dayjs(dateRange[1]).endOf("day").format("YYYY-MM-DD"),
          ] as [string, string],
        },
      ],
      filters,
    }
    // handleKey stands in for the handles array so a re-render with an
    // equivalent list doesn't rebuild the query object and refire the request.
  }, [handleKey, dateRange, matchType])

  const { resultSet, isLoading, error } = useCubeQuery(query, {
    skip: !enabled || handles.length === 0,
  })

  const byHandle = useMemo<TAdMetricsByHandle>(() => {
    if (!resultSet) return {}

    return resultSet.rawData().reduce((acc: TAdMetricsByHandle, row) => {
      const handle = String(row[`${PREFIX}.product_handle`] ?? "")
      if (!handle) return acc

      const metrics: Record<string, unknown> = {}
      AD_METRIC_KEYS.forEach(metric => {
        metrics[metric] = row[`${PREFIX}.${metric}`]
      })
      acc[handle] = metrics
      return acc
    }, {})
  }, [resultSet])

  return {
    adMetricsByHandle: byHandle,
    isLoading,
    // A missing ad-product table for this tenant surfaces as a skipped query
    // (empty result), not an error, so callers don't need to special-case it.
    error,
  }
}
